import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'clickorico:admin-sidebar-sections-collapsed';

function readCollapsedState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function persistCollapsedState(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* quota / private mode */
  }
}

/**
 * Ouverture affichée : préférence localStorage, sauf sections non collapsibles.
 * La section active peut rester fermée (Lot B — option 3).
 *
 * @param {string} sectionId
 * @param {{ collapsibleSectionIds: string[], collapsed: Record<string, boolean> }} ctx
 */
export function computeIsSectionOpen(sectionId, { collapsibleSectionIds, collapsed }) {
  if (!collapsibleSectionIds.includes(sectionId)) return true;
  return !collapsed[sectionId];
}

/**
 * À l’entrée dans une section (changement de activeSectionId), forcer l’ouverture
 * en mémoire / localStorage. Ne s’applique pas aux rerenders tant que l’id est stable.
 *
 * @param {Record<string, boolean>} collapsed
 * @param {string | null} activeSectionId
 * @param {string[]} collapsibleSectionIds
 */
export function applyActiveSectionAutoOpen(collapsed, activeSectionId, collapsibleSectionIds) {
  if (!activeSectionId || !collapsibleSectionIds.includes(activeSectionId)) {
    return collapsed;
  }
  if (!collapsed[activeSectionId]) return collapsed;
  return { ...collapsed, [activeSectionId]: false };
}

/**
 * Section active actuellement repliée par l’utilisateur — base pour un indicateur UI futur.
 *
 * @param {string | null} activeSectionId
 * @param {string[]} collapsibleSectionIds
 * @param {Record<string, boolean>} collapsed
 */
export function computeIsActiveSectionCollapsed(
  activeSectionId,
  collapsibleSectionIds,
  collapsed,
) {
  if (!activeSectionId || !collapsibleSectionIds.includes(activeSectionId)) return false;
  return Boolean(collapsed[activeSectionId]);
}

/**
 * @param {string[]} collapsibleSectionIds
 * @param {string | null} activeSectionId
 */
export function useSidebarSectionCollapse(collapsibleSectionIds, activeSectionId) {
  const [collapsed, setCollapsed] = useState(readCollapsedState);

  useEffect(() => {
    setCollapsed((prev) => {
      const next = applyActiveSectionAutoOpen(prev, activeSectionId, collapsibleSectionIds);
      if (next === prev) return prev;
      persistCollapsedState(next);
      return next;
    });
  }, [activeSectionId, collapsibleSectionIds]);

  const isSectionOpen = useCallback(
    (sectionId) =>
      computeIsSectionOpen(sectionId, { collapsibleSectionIds, collapsed }),
    [collapsed, collapsibleSectionIds],
  );

  const toggleSection = useCallback(
    (sectionId) => {
      if (!collapsibleSectionIds.includes(sectionId)) return;

      setCollapsed((prev) => {
        const next = { ...prev, [sectionId]: !prev[sectionId] };
        persistCollapsedState(next);
        return next;
      });
    },
    [collapsibleSectionIds],
  );

  const isActiveSectionCollapsed = computeIsActiveSectionCollapsed(
    activeSectionId,
    collapsibleSectionIds,
    collapsed,
  );

  return { isSectionOpen, toggleSection, isActiveSectionCollapsed };
}

export { STORAGE_KEY };
